import { Collaborate, CreditCard, EnterpriseOrganization, Grid, Settings } from '@/assets/icons'
import type { SettingsSection, SettingsSectionId } from '@/settings/components/settings-modal-types'

// Re-export the FE-827 shell's section types so feature modules import them from one place.
export type { SettingsSection, SettingsSectionId } from '@/settings/components/settings-modal-types'

// Group 1 = account-wide sections; group 2 = role-specific options (the FE-827 nav draws a divider
// between groups). Icons match the shipped SETTINGS_SECTIONS; members reuse the collaborate glyph.
const PREFERENCES: SettingsSection = { id: 'preferences', label: 'Preferences', icon: Settings, group: 1 }
const CONNECTED_ACCOUNTS: SettingsSection = { id: 'connected-accounts', label: 'Connected accounts', icon: Grid, group: 1 }
const SUBSCRIPTION: SettingsSection = { id: 'subscription', label: 'Subscription', icon: CreditCard, group: 2 }
const CREATE_ORGANIZATION: SettingsSection = { id: 'create-organization', label: 'Create organization', icon: EnterpriseOrganization, group: 2 }
const MEMBERS: SettingsSection = { id: 'members', label: 'Members', icon: Collaborate, group: 2 }
const BILLING: SettingsSection = { id: 'billing', label: 'Billing', icon: CreditCard, group: 2 }

export type BuildSettingsSectionsInput = {
  isOrgAccount: boolean
  isOrgAdmin: boolean
  /** Connected accounts is additionally gated by the DRIVE_SYNC flag, as the legacy page is today. */
  driveSyncEnabled: boolean
}

/**
 * Role-aware section list for the FE-827 SettingsModal shell. No React, no store — cheap to unit test.
 * Mirrors the confirmed IA (Profile/Account Security omitted in Release 1):
 *  - Individual:  Preferences, Connected accounts | Subscription, Create organization
 *  - Org admin:   Preferences, Connected accounts | Members, Billing
 *  - Org member:  Preferences, Connected accounts | Members (read-only)
 */
export function buildSettingsSections({ isOrgAccount, isOrgAdmin, driveSyncEnabled }: BuildSettingsSectionsInput): SettingsSection[] {
  const personal: SettingsSection[] = [PREFERENCES, ...(driveSyncEnabled ? [CONNECTED_ACCOUNTS] : [])]

  if (!isOrgAccount) {
    return [...personal, SUBSCRIPTION, CREATE_ORGANIZATION]
  }

  // Billing is admin-only; members see just the roster.
  return [...personal, ...(isOrgAdmin ? [MEMBERS, BILLING] : [MEMBERS])]
}

/**
 * Label for a nav group header, per the Figma per-role nav. Group 1 is the user's own settings
 * ("Personal settings" for org accounts, where it sits beside the org group; plain "Settings" for
 * individuals, who have no org group). Group 2 is the org's own section, labeled with the org name
 * for org accounts; for individuals it's the unlabeled role-options group (Subscription, Create
 * organization), so it returns `null` and renders with just the divider. Rendered uppercase by the nav.
 */
export function getSettingsGroupLabel(group: number, ctx: { isOrgAccount: boolean; orgName: string | null }): string | null {
  if (group === 1) return ctx.isOrgAccount ? 'Personal settings' : 'Settings'
  if (group === 2) return ctx.isOrgAccount ? ctx.orgName?.trim() || 'Organization' : null
  return null
}

/** First section for the user's role — the default when none is specified (derive, don't store). */
export function getDefaultSectionId(sections: SettingsSection[]): SettingsSectionId | null {
  return sections[0]?.id ?? null
}

/** Whether a (possibly stale) section id is valid for the current role's sections. */
export function isSectionAvailable(sections: SettingsSection[], id: string | null): id is SettingsSectionId {
  return id !== null && sections.some((section) => section.id === id)
}
